import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Box, Button, Typography } from "@mui/material";
import { FilePond, registerPlugin, FileStatus } from "react-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import { useNotify } from "react-admin";

import "filepond/dist/filepond.min.css";
import { DropzoneDocumentType, getDocumentTypeConfig, FILE_CONSTANTS } from "@/constants/documentUpload";
import { uploadDocument } from "@/providers/uploadService";
import {
  AlertType,
  DocumentRecordType,
  DocumentUploadStatus,
  FileAssignment,
  FileUploadStatus,
  MultiUploadResultStatus,
  UploadResultStatus,
  ValidAny,
  type DocumentRead,
  type DocumentUploadItem,
  type PolicyVendorContext,
  type TargetSchema,
} from "@/types";
import {
  VendorAssignmentDialog,
  VendorAssignmentPanel,
  type FileClientChoice,
  type VendorAssignmentType,
  useVendorOptions,
} from "./VendorAssignmentDialog";
import { trackEvent, trackError, setTag } from "@/utils/clarityTracking";
import { detectFileType } from "@/utils/fileTypeDetection";
import { featureFlags } from "@/utils/featureFlags";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { SHEET_DISCOVERY_MAX_BYTES, discoverSheetNames } from "./sheetDiscovery";
import { formatMiB, resolveUploadLimit } from "@/constants/uploadLimits";

/** Human names for the accepted extensions, de-duplicated and in display order. */
const TYPE_NAMES: Record<string, string> = {
  ".pdf": "PDF",
  ".doc": "Word",
  ".docx": "Word",
  ".xls": "Excel",
  ".xlsx": "Excel",
  ".csv": "CSV",
};

/**
 * The drop target's second line (#7551 AC5): "PDF, Word, Excel or CSV · PDF up to
 * 100 MB, Excel 50 MB, CSV 25 MB · up to 20 files". Caps come from the per-type
 * upload limits so the promise matches what the server enforces. Pure for tests.
 */
export const buildDropHint = (extensions: readonly string[], maxFiles: number): string => {
  // Caps are per format (ADR-038): a single "up to X MB" would promise a CSV
  // user the PDF ceiling and hand them a 413 at a quarter of it. Group formats
  // that share a cap so the line stays short: "PDF 100 MB · Word, Excel 50 MB · CSV 25 MB".
  const byCap = new Map<number, string[]>();
  for (const ext of extensions) {
    const name = TYPE_NAMES[ext] ?? ext.replace(".", "").toUpperCase();
    const cap = resolveUploadLimit(`x${ext}`).bytes;
    const names = byCap.get(cap) ?? [];
    if (!names.includes(name)) names.push(name);
    byCap.set(cap, names);
  }
  const caps = [...byCap.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([cap, names]) => `${names.join(", ")} ${formatMiB(cap)} MB`);
  return `${caps.join(" · ")} · up to ${maxFiles} files`;
};

const buildAssignment = (item: ValidAny, sheetNames: string[] | undefined): FileAssignment => {
  const file = item.file as File;
  return {
    id: item.id || Math.random().toString(36).slice(2, 11),
    file,
    filename: item.filename || file.name,
    size: file.size,
    type: detectFileType(file) || file.type,
    status: FileUploadStatus.PENDING,
    sheetNames,
  };
};

/** One file × one sheet selection. Pure-data planning unit consumed by handleUpload. */
export type UploadTask = {
  assignment: FileAssignment;
  sheetName?: string;
};

/** Per-task outcome produced inside handleUpload and consumed by tallyTaskResults. */
export type TaskResult = {
  fileId: string;
  sheetName?: string;
  status: UploadResultStatus;
  error?: string;
  /** The 202 body — the created document record — on success. */
  document?: DocumentRead;
};

/** Per-file aggregate across that file's sheet tasks. */
export type FileTally = {
  success: number;
  failed: number;
  firstError?: string;
};

/**
 * Fan a file list out into per-sheet upload tasks. Files with at least one
 * entry in selectedSheets produce N tasks (one per sheet); files without
 * any selected sheets — single-sheet workbooks, CSVs, PDFs — produce a
 * single task with sheetName=undefined so the legacy no-header upload path
 * fires unchanged. MSW-5 / ADO #4444.
 */
export const expandUploadTasks = (files: FileAssignment[]): UploadTask[] =>
  files.flatMap((assignment) => {
    const sheets = assignment.selectedSheets ?? [];
    if (sheets.length === 0) {
      return [{ assignment, sheetName: undefined }];
    }
    return sheets.map((sheetName) => ({ assignment, sheetName }));
  });

/**
 * Aggregate Promise.allSettled task outcomes into per-file tallies.
 * A file is COMPLETED iff every one of its sheets succeeded; any failure
 * flips it to ERROR with the first failure's message preserved. REJECTED
 * results (sync throw in the task wrapper) are mapped back to their file
 * via task index → tasks[i].assignment.id and counted as failures, so a
 * future regression in convertToDocumentUploadItem or its callers surfaces
 * on the row instead of silently dropping. MSW-5 / ADO #4444 AC5.
 */
export const tallyTaskResults = (
  tasks: UploadTask[],
  results: PromiseSettledResult<TaskResult>[]
): { perFile: Map<string, FileTally>; sheetSuccess: number; sheetFailed: number } => {
  const perFile = new Map<string, FileTally>();
  let sheetSuccess = 0;
  let sheetFailed = 0;

  for (let i = 0; i < results.length; i++) {
    const r = results[i];
    let fileId: string;
    let succeeded: boolean;
    let error: string | undefined;

    if (r.status === MultiUploadResultStatus.FULFILLED) {
      fileId = r.value.fileId;
      succeeded = r.value.status === UploadResultStatus.SUCCESS;
      error = r.value.error;
    } else {
      fileId = tasks[i].assignment.id;
      succeeded = false;
      error = r.reason instanceof Error ? r.reason.message : "Upload failed";
    }

    const entry = perFile.get(fileId) ?? { success: 0, failed: 0 };
    if (succeeded) {
      entry.success++;
      sheetSuccess++;
    } else {
      entry.failed++;
      if (entry.firstError === undefined) entry.firstError = error;
      sheetFailed++;
    }
    perFile.set(fileId, entry);
  }

  return { perFile, sheetSuccess, sheetFailed };
};

/**
 * The documents a batch actually created, in task order. Fulfilled + SUCCESS
 * tasks whose 202 body carried an id; failures and rejected tasks contribute
 * nothing. The dropzone hands this to `onUploadComplete` so a caller can land on
 * the one document a single-file upload created instead of the list.
 */
export const collectUploadedDocuments = (results: PromiseSettledResult<TaskResult>[]): DocumentRead[] =>
  results.flatMap((r) =>
    r.status === MultiUploadResultStatus.FULFILLED &&
    r.value.status === UploadResultStatus.SUCCESS &&
    r.value.document?.id
      ? [r.value.document]
      : []
  );

/**
 * Per-sheet wording is only used in the summary toast when every file in
 * the batch fans out into sheet tasks. Mixed batches (e.g. a 2-sheet xlsx
 * dropped alongside a PDF) keep file wording so the PDF isn't mislabeled
 * as a sheet. MSW-5 / ADO #4444.
 */
export const isPerSheetBatch = (files: FileAssignment[], tasks: UploadTask[]): boolean =>
  tasks.length > files.length && files.every((f) => (f.selectedSheets?.length ?? 0) > 0);

registerPlugin(FilePondPluginFileValidateType);

/**
 * The client a file binds to (#7551): the row's own choice when the user made one
 * (`null` = explicitly none), else the batch's client from the dialog, else the
 * caller's `clientIdRef` for dropzones that never show a client line. Pure so the
 * per-file passthrough is unit-tested without FilePond.
 */
export const resolveFileClientId = (
  fileId: string,
  ctx: {
    fileClients: Record<string, FileClientChoice | null>;
    defaultClient: FileClientChoice | null | undefined;
    clientIdRef: string | undefined;
  }
): string | undefined => {
  if (fileId in ctx.fileClients) return ctx.fileClients[fileId]?.id;
  return ctx.defaultClient === undefined ? ctx.clientIdRef : (ctx.defaultClient?.id ?? undefined);
};

interface DocumentDropzoneProps {
  /** Type of documents being uploaded (CLAIMS, POLICIES, etc.) */
  documentType: DropzoneDocumentType;
  /** Target schema for document transformation */
  targetSchema: TargetSchema;
  /**
   * Fires once the batch settles (any outcome). `uploaded` is the documents the
   * batch created — one per successful file or sheet — so the caller can route
   * on the count (one document → its show page; several → the list).
   */
  onUploadComplete?: (uploaded: DocumentRead[]) => void;
  /** Custom drop zone label (overrides default) */
  label?: string;
  /** Maximum files allowed (overrides document type default) */
  maxFiles?: number;
  /** Accepted file types (overrides document type default) */
  acceptedFileTypes?: string[];
  /** File validation error message */
  validationMessage?: string;
  /** Pre-select this vendor for all files in the assignment dialog */
  defaultVendorId?: string;
  /** Policy vendor context for the claims selector: pins policy vendors, tags run-out, pre-fills primary TPA (#5591). */
  policyVendors?: PolicyVendorContext;
  /** Names the source of the claims prefill (policy id, or the client's administrators) for the assignment dialog. */
  defaultSourceLabel?: string;
  /**
   * Fires when the prepare step opens/closes. In `prepareInline` mode the parent
   * dialog switches its own chrome (title, type/client controls) on this; in the
   * default mode it lets a parent dialog hide itself so two modals don't stack.
   */
  onAssignmentDialogChange?: (open: boolean) => void;
  /** Fires when an upload starts / settles (inline prepare), so the dialog can refuse to close meanwhile (#7551 AC11). */
  onUploadingChange?: (uploading: boolean) => void;
  /** False hides the per-row client Change link: the client is fixed for the batch (#7551 AC3). Default true. */
  fileClientEditable?: boolean;
  /** Pre-populate client ID ref on uploaded documents (e.g. when uploading from client screen) */
  clientIdRef?: string;
  /**
   * The batch was declared a book-of-business upload (#7551, "Multiple clients in
   * one file"): every file arrives with `is_multi_client` set so the prepare row's
   * checkbox shows it and the upload carries X-Multi-Client; rows can still untick.
   */
  defaultMultiClient?: boolean;
  /**
   * Render the prepare step as a panel in place of the drop target (#7551) instead
   * of opening its own Dialog. Back returns to the drop target with the files kept;
   * "Add more files" shows the drop target under the rows.
   */
  prepareInline?: boolean;
  /**
   * The client the batch binds to by default (#7551), shown on every prepare row
   * with a Change link. Undefined → the rows show no client line (callers that
   * bind via `clientIdRef` only, e.g. the policy page). `null` → no client, rows
   * read "Chosen at review".
   */
  defaultClient?: FileClientChoice | null;
  /**
   * Multi-client ("book of business") reimbursement upload (#5309 / ADR-057):
   * sends X-Multi-Client and routes the document onto the multi-client load +
   * review path (each row resolves its own client/policy). When set, no single
   * client is bound. Defaults to false (single-client path).
   */
  isMultiClient?: boolean;
}

export interface DocumentDropzoneRef {
  /** Clear all selected files from the dropzone */
  clearFiles: () => void;
}

interface DocumentTypeSettings {
  recordType: DocumentRecordType;
  vendorType: VendorAssignmentType;
}

/**
 * Gets document type-specific settings for record type and vendor type.
 */
const getDocumentTypeSettings = (documentType: DropzoneDocumentType): DocumentTypeSettings => {
  // Partial: ATTACHMENTS (#6506) has no DocumentDropzone config — the claimant
  // uploader is hand-rolled and never mounts this dropzone. The `|| settings.CLAIMS`
  // fallback below keeps the return non-undefined, so we don't invent a bogus
  // attachment entry just to satisfy an exhaustive Record.
  const settings: Partial<Record<DropzoneDocumentType, DocumentTypeSettings>> = {
    CLAIMS: {
      recordType: DocumentRecordType.STOPLOSS_CLAIMS,
      // TPA + RX union (AB#5312): claims may be administered by a TPA or a PBM/RX vendor.
      vendorType: "tpaRx",
    },
    POLICIES: {
      recordType: DocumentRecordType.POLICIES,
      vendorType: "carrier",
    },
    REIMBURSEMENTS: {
      recordType: DocumentRecordType.REIMBURSEMENTS,
      vendorType: "carrier",
    },
  };

  // CLAIMS is always present in the literal above; the assertion keeps the
  // historical fallback for any unmapped type (e.g. ATTACHMENTS, which never
  // mounts this dropzone) while satisfying the non-optional return type.
  return settings[documentType] ?? (settings.CLAIMS as DocumentTypeSettings);
};

/**
 * Build the upload payload for a single assigned file. Pure + exported so the
 * multi-client routing decision is unit-testable in isolation (#5321): the
 * per-file `is_multi_client` flag from the Prepare-upload dialog takes
 * precedence over the component-level `isMultiClient` fallback, and a
 * multi-client upload binds NO single client — `client_id_ref` is suppressed so
 * each row resolves its own client on load.
 */
export const buildDocumentUploadItem = (
  assignment: FileAssignment,
  opts: { recordType: DocumentRecordType; isMultiClient?: boolean; clientIdRef?: string }
): DocumentUploadItem => {
  const multiClient = assignment.is_multi_client ?? opts.isMultiClient;
  return {
    id: assignment.id,
    file: assignment.file,
    name: assignment.filename,
    record_type: opts.recordType,
    type: "source",
    version: 1,
    vendor_id_ref: assignment.carrierId || undefined,
    client_id_ref: multiClient ? undefined : opts.clientIdRef,
    is_multi_client: multiClient || undefined,
    mime_type: assignment.type || detectFileType(assignment.file) || "application/octet-stream",
    status: DocumentUploadStatus.PENDING,
    progress: 0,
    error: null,
  };
};

/**
 * A drag-and-drop file upload component that handles document upload workflow with vendor assignment.
 *
 * Features:
 * - File validation based on document type
 * - Vendor assignment dialog (TPA or Carrier) for each uploaded file
 * - Progress tracking and error handling
 * - Integration with React Admin for notifications
 * - Clarity UX tracking for analytics
 *
 * @example
 * ```tsx
 * <DocumentDropzone
 *   documentType={DropzoneDocumentType.CLAIMS}
 *   targetSchema={TargetSchema.STOPLOSS_CLAIM}
 *   onUploadComplete={() => console.log('Upload complete!')}
 * />
 * ```
 */
export const DocumentDropzone = forwardRef<DocumentDropzoneRef, DocumentDropzoneProps>(
  (
    {
      documentType,
      targetSchema,
      onUploadComplete,
      label,
      maxFiles,
      acceptedFileTypes,
      validationMessage,
      defaultVendorId,
      policyVendors,
      defaultSourceLabel,
      onAssignmentDialogChange,
      onUploadingChange,
      fileClientEditable = true,
      clientIdRef,
      prepareInline = false,
      defaultClient,
      isMultiClient,
      defaultMultiClient = false,
    },
    ref
  ) => {
    const notify = useNotify();
    const notifyError = useRichApiErrorNotify();
    const [selectedFiles, setSelectedFiles] = useState<ValidAny[]>([]);
    const [fileAssignments, setFileAssignments] = useState<FileAssignment[]>([]);
    const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
    // Per-file client overrides (#7551): absent key → the batch default. `null` =
    // the user chose none (or the file is multi-client).
    const [fileClients, setFileClients] = useState<Record<string, FileClientChoice | null>>({});
    // Inline mode: the drop target is showing under the prepare rows.
    const [addingMore, setAddingMore] = useState(false);

    // Report the assignment dialog's visibility to a parent dialog so it can hide
    // itself while the "Prepare upload" step is showing (avoids stacked modals).
    // Also signals closed on unmount so a parent can't get stuck hidden.
    useEffect(() => {
      onAssignmentDialogChange?.(showAssignmentDialog);
      return () => onAssignmentDialogChange?.(false);
    }, [showAssignmentDialog, onAssignmentDialogChange]);

    // FilePond fires onupdatefiles multiple times per drop (DID_ADD_ITEM,
    // then COMPLETE_LOAD_ITEM). This ref records every file id we have
    // already routed through discovery + toasting so each fire after the
    // first becomes a no-op for the same files. Entries are pruned when
    // the user removes or clears the file.
    const processedFileIdsRef = useRef<Set<string>>(new Set());

    // Get document type configuration
    const config = getDocumentTypeConfig(documentType);
    const typeSettings = getDocumentTypeSettings(documentType);

    // Inline mode: warm the vendor list while the user is still choosing files, so
    // the prepare rows paint their administrator radios on first render instead
    // of flickering from search field → radios when the list arrives.
    useVendorOptions(typeSettings.vendorType, prepareInline);

    // Use props or fall back to document type config or defaults
    const finalMaxFiles = maxFiles || config?.MAX_FILES || 20;
    // Inline mode states the rules up front (#7551 AC5) instead of after a rejection.
    const dropHint = config ? buildDropHint(config.ACCEPTED_EXTENSIONS, finalMaxFiles) : null;
    const finalLabel =
      label ||
      (prepareInline && dropHint
        ? `Drop files here or <span class="filepond--label-action">browse</span>` +
          `<span class="dropzone-hint">${dropHint}</span>`
        : config?.LABEL || 'Drag & drop your documents or <span class="filepond--label-action">browse</span>');
    const finalAcceptedTypes =
      acceptedFileTypes || (config ? [...config.ACCEPTED_MIME_TYPES] : [...FILE_CONSTANTS.ACCEPTED_MIME_TYPES]);
    const finalValidationMessage =
      validationMessage || config?.VALIDATION_MESSAGE || "Expects PDF, DOC, DOCX, XLS, or XLSX";

    /** Handles file selection from FilePond and opens vendor assignment dialog */
    const handleFilesUpdate = async (fileItems: ValidAny[]) => {
      setSelectedFiles(fileItems);

      // Partition the current FilePond list into items that pass FilePond's
      // built-in validation and items it has flagged. FilePond emits
      // onupdatefiles multiple times per drop (DID_ADD_ITEM, then
      // COMPLETE_LOAD_ITEM); the ref below filters everything except newly
      // seen ids so discovery and toasts each happen once per file.
      const ERROR_STATUSES = new Set([
        FileStatus.PROCESSING_ERROR,
        FileStatus.LOAD_ERROR,
        FileStatus.PROCESSING_REVERT_ERROR,
      ]);
      const isInvalid = (item: ValidAny) =>
        !item.file || ERROR_STATUSES.has(item.status) || item.status === AlertType.ERROR;
      const validFiles = fileItems.filter((item) => !isInvalid(item));
      const invalidFiles = fileItems.filter((item) => isInvalid(item));

      const processed = processedFileIdsRef.current;
      const presentIds = new Set(fileItems.map((it) => it.id));
      for (const id of [...processed]) {
        if (!presentIds.has(id)) processed.delete(id);
      }
      // Reflect FilePond removals in the assignment list immediately so the
      // dialog stays in sync when the user clears a file mid-flight.
      // Keep the same array/object when nothing was removed: FilePond fires this
      // twice per drop, and a fresh identity would reset the panel's rows (and
      // their pre-filled administrator) on every fire.
      setFileAssignments((prev) => {
        const next = prev.filter((a) => presentIds.has(a.id));
        return next.length === prev.length ? prev : next;
      });
      setFileClients((prev) => {
        const kept = Object.entries(prev).filter(([id]) => presentIds.has(id));
        if (kept.length === Object.keys(prev).length) return prev;
        return Object.fromEntries(kept);
      });

      const newInvalidFiles = invalidFiles.filter((item) => !processed.has(item.id));
      const newValidFiles = validFiles.filter((item) => !processed.has(item.id));
      for (const item of newInvalidFiles) processed.add(item.id);

      if (newInvalidFiles.length > 0) {
        trackError("document_upload", "file_type_rejected");
        trackEvent("document_upload_validation_failed");
        setTag("document_type", documentType);
        setTag("rejected_count", String(newInvalidFiles.length));
        notify(`${newInvalidFiles.length} file(s) rejected: Invalid file type. ${finalValidationMessage}`, {
          type: AlertType.ERROR,
        });
      }

      if (newValidFiles.length === 0) {
        return;
      }
      for (const item of newValidFiles) processed.add(item.id);

      // Per-file-type upload caps — see @/constants/uploadLimits, which is the
      // single client-side place these are set (per environment, via VITE_*).
      // The server is authoritative and refuses over-cap files with 413 /
      // VAL-000003; checking here turns a slow, opaque failure into an
      // immediate, specific one naming the file's own limit.
      //
      // Applied to EVERY file type. The old flow size-checked only Excel — the
      // ceiling sat behind isExcelFile() — so an over-cap CSV or PDF uploaded
      // in full and then 413'd with a bare "All uploads failed" (AB#6463).
      const overCap = newValidFiles.filter((item) => {
        const file = item.file as File;
        return file.size > resolveUploadLimit(file.name).bytes;
      });

      if (overCap.length > 0) {
        trackError("document_upload", "file_over_type_limit");
        trackEvent("document_upload_over_type_limit");
        setTag("document_type", documentType);
        setTag("over_cap_count", String(overCap.length));
        const detail = overCap
          .map((item) => {
            const file = item.file as File;
            const { extension, bytes } = resolveUploadLimit(file.name);
            const label = extension ? extension.toUpperCase() : "this file type";
            return `${file.name} is ${formatMiB(file.size)} MB (limit for ${label} is ${formatMiB(bytes)} MB)`;
          })
          .join("; ");
        notify(
          `${overCap.length} ${overCap.length === 1 ? "file was" : "files were"} not uploaded — ${detail}.`,
          // Long-lived and explicitly dismissable: the previous 4s default made
          // this class of rejection easy to miss entirely (AB#6463).
          { type: AlertType.ERROR, autoHideDuration: 15000 }
        );
      }

      const overCapIds = new Set(overCap.map((item) => item.id));
      const withinCap = newValidFiles.filter((item) => !overCapIds.has(item.id));
      if (withinCap.length === 0) {
        return;
      }

      let newAssignments: FileAssignment[];

      if (featureFlags.multiSheetWorkbookEnabled) {
        // Resolve each newly seen file through client-side sheet discovery.
        // Password-protected and unparseable workbooks are rejected up front —
        // they would surface a worse failure at the server-side worker layer
        // otherwise. Non-Excel files pass through unchanged (no sheet picker
        // needed).
        //
        // Oversized workbooks are NOT rejected (AB#6463). The size ceiling
        // exists to keep XLSX.read off the main thread, not to police uploads;
        // the real per-file-type cap is the server's and is enforced with 413.
        // So we keep the file with sheetNames undefined — same shape non-Excel
        // files use — and tell the user the sheet picker is unavailable.
        type Resolved =
          | { kind: "keep"; assignment: FileAssignment; discoverySkipped: boolean }
          | { kind: "reject"; reason: "password_protected" | "unparseable" };

        const resolved: Resolved[] = await Promise.all(
          withinCap.map(async (item): Promise<Resolved> => {
            const file = item.file as File;
            const result = await discoverSheetNames(file);
            if (result.status === "password_protected" || result.status === "unparseable") {
              return { kind: "reject", reason: result.status };
            }
            return {
              kind: "keep",
              assignment: buildAssignment(item, result.status === "ok" ? result.sheetNames : undefined),
              discoverySkipped: result.status === "too_large",
            };
          })
        );

        newAssignments = resolved.flatMap((r) => (r.kind === "keep" ? [r.assignment] : []));

        // Oversized workbooks are kept, so this is a notice rather than an
        // error: the upload proceeds, only the sheet picker is unavailable.
        const discoverySkippedCount = resolved.filter((r) => r.kind === "keep" && r.discoverySkipped).length;
        if (discoverySkippedCount > 0) {
          trackEvent("document_upload_sheet_discovery_skipped");
          setTag("document_type", documentType);
          setTag("discovery_skipped_count", String(discoverySkippedCount));
          notify(
            `${discoverySkippedCount} ${discoverySkippedCount === 1 ? "workbook is" : "workbooks are"} over ` +
              `${formatMiB(SHEET_DISCOVERY_MAX_BYTES)} MB, so sheet selection is unavailable — ` +
              `the first sheet will be used. The upload will continue.`,
            { type: AlertType.INFO }
          );
        }

        const rejectionCounts = { password_protected: 0, unparseable: 0 };
        for (const r of resolved) {
          if (r.kind === "reject") rejectionCounts[r.reason]++;
        }

        const rejectionMessages: Array<{ count: number; reason: string; message: (n: number) => string }> = [
          {
            count: rejectionCounts.password_protected,
            reason: "password_protected",
            message: (n) =>
              `${n} password-protected ${n === 1 ? "file" : "files"} skipped — remove the password and re-upload.`,
          },
          {
            count: rejectionCounts.unparseable,
            reason: "unparseable",
            message: (n) =>
              `${n} unreadable ${n === 1 ? "file" : "files"} skipped — try saving a fresh copy of the workbook.`,
          },
        ];
        for (const { count, reason, message } of rejectionMessages) {
          if (count > 0) {
            trackError("document_upload", `sheet_discovery_${reason}`);
            trackEvent("document_upload_workbook_rejected");
            setTag("document_type", documentType);
            setTag("rejection_reason", reason);
            setTag("rejected_count", String(count));
            notify(message(count), { type: AlertType.ERROR });
          }
        }
      } else {
        // Multi-sheet workbook handling disabled. Run discovery to
        // distinguish single-sheet workbooks (allowed, pre-MSW path) from
        // multi-sheet workbooks (blocked with a "coming soon" notice).
        // Oversized / password-protected / unparseable workbooks fall
        // back to the pre-MSW pass-through — those rejection toasts are
        // part of the MSW feature and only fire when the flag is on.
        const decisions = await Promise.all(
          withinCap.map(async (item) => {
            const result = await discoverSheetNames(item.file as File);
            const isMultiSheet = result.status === "ok" && result.sheetNames.length > 1;
            return { item, isMultiSheet };
          })
        );

        const blockedCount = decisions.filter((d) => d.isMultiSheet).length;
        if (blockedCount > 0) {
          trackEvent("document_upload_workbook_blocked_flag_off");
          setTag("document_type", documentType);
          setTag("workbook_blocked_count", String(blockedCount));
          notify(
            `${blockedCount} ${blockedCount === 1 ? "workbook" : "workbooks"} skipped — multi-sheet uploads are coming soon.`,
            { type: AlertType.INFO }
          );
        }

        newAssignments = decisions.flatMap((d) => (d.isMultiSheet ? [] : [buildAssignment(d.item, undefined)]));
      }

      // Guard against a race: if the user removed a file from FilePond
      // while discoverSheetNames was in flight, the second onupdatefiles
      // fire pruned its id from the ref. Re-check ref membership before
      // committing the result so the removed file does not surface as a
      // ghost assignment.
      const stillPresent = newAssignments
        .filter((a) => processedFileIdsRef.current.has(a.id))
        .map((a) => (defaultMultiClient ? { ...a, is_multi_client: true } : a));
      if (stillPresent.length > 0) {
        setFileAssignments((prev) => [...prev, ...stillPresent]);
        trackEvent("document_upload_files_selected");
        setTag("document_type", documentType);
        setTag("file_count", String(stillPresent.length));
        setShowAssignmentDialog(true);
        setAddingMore(false);
      }
    };

    // Close the assignment dialog when the file list empties (e.g. user
    // removed the last file from the FilePond panel). Handlers that
    // explicitly tear down state (handleCloseDialog, handleUpload) still
    // close the dialog directly; this is the fallback for FilePond-driven
    // removals.
    useEffect(() => {
      if (fileAssignments.length === 0 && showAssignmentDialog) {
        setShowAssignmentDialog(false);
      }
    }, [fileAssignments, showAssignmentDialog]);

    /** Removes a file from the assignment list */
    const handleRemoveFile = (fileId: string) => {
      // Track file removal in Clarity
      trackEvent("document_upload_file_removed");
      setTag("document_type", documentType);

      setFileAssignments((prev) => prev.filter((a) => a.id !== fileId));
    };

    /** Converts FileAssignment to DocumentUploadItem for upload service */
    const convertToDocumentUploadItem = (assignment: FileAssignment): DocumentUploadItem =>
      buildDocumentUploadItem(assignment, {
        recordType: typeSettings.recordType,
        isMultiClient,
        clientIdRef: resolveFileClientId(assignment.id, { fileClients, defaultClient, clientIdRef }),
      });

    /** Handles the complete upload workflow for all assigned files */
    // TODO: p-limit if backend load becomes an issue — every task fires in
    // parallel. Fine for the 5/20 single-workbook realistic max; concern is
    // batch ingestion where N workbooks × M sheets compound.
    const handleUpload = async (files: FileAssignment[]) => {
      // Expand each file into per-sheet upload tasks. Multi-sheet workbooks
      // with selectedSheets produce N tasks (one per sheet, each carrying
      // X-Sheet-Name); single-sheet workbooks / CSV / PDF produce one task
      // with sheetName=undefined so the legacy no-header path fires
      // unchanged. MSW-5 / ADO #4444.
      const tasks = expandUploadTasks(files);

      // Track upload started in Clarity
      trackEvent("document_upload_started");
      setTag("document_type", documentType);
      setTag("upload_file_count", String(files.length));
      setTag("upload_task_count", String(tasks.length));

      // Set all files to uploading state
      setFileAssignments((prev) => prev.map((file) => ({ ...file, status: FileUploadStatus.UPLOADING })));

      try {
        // Fire every task in parallel. allSettled (not all) so a single sheet
        // failure does NOT short-circuit sibling sheet uploads — AC5 / #4444.
        const results = await Promise.allSettled(
          tasks.map(async ({ assignment, sheetName }): Promise<TaskResult> => {
            const label = sheetName ? `${assignment.filename} [${sheetName}]` : assignment.filename;
            try {
              const documentItem = convertToDocumentUploadItem(assignment);

              const created = await uploadDocument(
                documentItem,
                undefined,
                (status, error) => {
                  console.log(`Upload status for ${label}: ${status}`, error || "");
                },
                sheetName
              );

              return { fileId: assignment.id, sheetName, status: UploadResultStatus.SUCCESS, document: created };
            } catch (error) {
              console.error(`Failed to upload ${label}:`, error);
              return {
                fileId: assignment.id,
                sheetName,
                status: UploadResultStatus.ERROR,
                error: error instanceof Error ? error.message : "Upload failed",
              };
            }
          })
        );

        const { perFile, sheetSuccess, sheetFailed } = tallyTaskResults(tasks, results);

        setFileAssignments((prev) =>
          prev.map((file) => {
            const tally = perFile.get(file.id);
            if (!tally) {
              return { ...file, status: FileUploadStatus.ERROR, error: "Error setting file statuses" };
            }
            if (tally.failed === 0) return { ...file, status: FileUploadStatus.COMPLETED };
            return { ...file, status: FileUploadStatus.ERROR, error: tally.firstError ?? "Upload failed" };
          })
        );

        const fileSuccess = Array.from(perFile.values()).filter((t) => t.failed === 0).length;
        const fileFailed = perFile.size - fileSuccess;
        const isPerSheet = isPerSheetBatch(files, tasks);

        if (sheetFailed === 0) {
          trackEvent("document_upload_success");
          setTag("document_type", documentType);
          setTag("success_count", String(sheetSuccess));

          notify(
            isPerSheet
              ? `All ${sheetSuccess} sheet${sheetSuccess !== 1 ? "s" : ""} uploaded successfully across ${fileSuccess} file${fileSuccess !== 1 ? "s" : ""}!`
              : `All ${fileSuccess} file${fileSuccess !== 1 ? "s" : ""} uploaded successfully!`,
            { type: AlertType.SUCCESS }
          );
        } else if (sheetSuccess > 0) {
          trackEvent("document_upload_partial_success");
          setTag("document_type", documentType);
          setTag("success_count", String(sheetSuccess));
          setTag("error_count", String(sheetFailed));

          notify(
            isPerSheet
              ? `${sheetSuccess} sheet${sheetSuccess !== 1 ? "s" : ""} uploaded, ${sheetFailed} failed`
              : `${fileSuccess} file${fileSuccess !== 1 ? "s" : ""} uploaded successfully, ${fileFailed} failed`,
            { type: AlertType.WARNING }
          );
        } else {
          trackError("document_upload", "all_failed");
          setTag("document_type", documentType);
          setTag("error_count", String(sheetFailed));

          notify("All uploads failed", { type: AlertType.ERROR });
        }

        // Cleanup immediately
        setShowAssignmentDialog(false);
        setFileAssignments([]);
        setFileClients({});
        clearFiles();
        onUploadComplete?.(collectUploadedDocuments(results));
      } catch (error) {
        // This catch is for unexpected errors in the overall process
        // Track unexpected error in Clarity
        trackError("document_upload", "unexpected_error");
        setTag("document_type", documentType);

        notifyError(error);

        // Cleanup immediately on failure too
        setShowAssignmentDialog(false);
        setFileAssignments([]);
        setFileClients({});
        clearFiles();
      }
    };

    /** Handles vendor assignment dialog close (cancellation) */
    const handleCloseDialog = () => {
      // Track dialog cancellation in Clarity
      trackEvent("document_upload_cancelled");
      setTag("document_type", documentType);

      setShowAssignmentDialog(false);
      setFileAssignments([]);
      setFileClients({});
      clearFiles();
    };

    /** Clear files (called by parent or internally) */
    const clearFiles = () => {
      // Wipe the processed-id ref so a subsequent drop that reuses an id
      // (browser/FilePond-version dependent) gets a fresh discovery pass.
      processedFileIdsRef.current.clear();
      setSelectedFiles([]);
    };

    // Expose clearFiles method via ref
    useImperativeHandle(ref, () => ({
      clearFiles,
    }));

    const dropTarget = (
      <Box
        sx={{
          p: prepareInline ? 2 : 3,
          mb: prepareInline ? 0 : 3,
          ...(prepareInline ? { bgcolor: "action.hover", borderRadius: 3 } : {}),
          border: "2px dashed",
          borderColor: "divider",
          borderRadius: 2,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
            ...(prepareInline ? {} : { transform: "translateY(-2px)", boxShadow: 2 }),
          },
          // Custom FilePond styling
          "& .filepond--root": {
            backgroundColor: "transparent",
            marginBottom: 0,
          },
          "& .filepond--panel-root": {
            backgroundColor: "transparent",
            border: "none",
          },
          "& .filepond--drop-label": {
            backgroundColor: "transparent",
            color: "text.primary",
            ...(prepareInline ? { minHeight: "128px" } : {}),
          },
          "& .filepond--drop-label label": prepareInline ? { fontSize: "1rem", fontWeight: 600 } : {},
          "& .dropzone-hint": {
            display: "block",
            marginTop: "8px",
            fontSize: "0.8125rem",
            fontWeight: 400,
            color: "text.secondary",
          },
        }}
      >
        <FilePond
          files={selectedFiles}
          onupdatefiles={handleFilesUpdate}
          allowMultiple={true}
          maxFiles={finalMaxFiles}
          name="files"
          labelIdle={finalLabel}
          acceptedFileTypes={finalAcceptedTypes}
          allowFileTypeValidation={true}
          labelFileTypeNotAllowed="File type not allowed"
          fileValidateTypeLabelExpectedTypes={finalValidationMessage}
          fileValidateTypeDetectType={(source: File, type: string) =>
            Promise.resolve(type || detectFileType(source) || "application/octet-stream")
          }
          credits={false}
        />
      </Box>
    );

    const panelProps = {
      open: showAssignmentDialog,
      files: fileAssignments,
      targetSchema,
      vendorType: typeSettings.vendorType,
      onClose: handleCloseDialog,
      onUpload: handleUpload,
      onRemoveFile: handleRemoveFile,
      defaultVendorId,
      policyVendors,
      defaultSourceLabel,
      defaultClient,
      fileClients,
      fileClientEditable,
      onUploadingChange,
      onFileClientChange:
        defaultClient === undefined
          ? undefined
          : (fileId: string, client: FileClientChoice | null) =>
              setFileClients((prev) => ({ ...prev, [fileId]: client })),
    };

    if (prepareInline) {
      // ONE tree position for FilePond across the whole flow: the panel is always
      // mounted and the drop target always lives in its add-more slot, shown when
      // the rows are hidden (drop step) or when adding more. Unmounting / moving
      // FilePond re-creates every item with a new id, which reads as "all files
      // removed, N new files added" and resets the rows (!4974 thread 31126); a
      // mounted panel also keeps the vendor picks across Back (#7551 AC7).
      const dropTargetVisible = !showAssignmentDialog || addingMore;
      return (
        <>
          <VendorAssignmentPanel
            {...panelProps}
            open={showAssignmentDialog || prepareInline}
            rowsVisible={showAssignmentDialog}
            onBack={() => {
              setAddingMore(false);
              setShowAssignmentDialog(false);
            }}
            onAddMore={() => setAddingMore(true)}
            addingMore={addingMore}
            addMoreSlot={<Box sx={{ display: dropTargetVisible ? "block" : "none" }}>{dropTarget}</Box>}
            onDoneAdding={() => setAddingMore(false)}
          />
          {!showAssignmentDialog && fileAssignments.length > 0 && (
            <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                {fileAssignments.length} file{fileAssignments.length !== 1 ? "s" : ""} ready to prepare
              </Typography>
              <Button size="small" onClick={() => setShowAssignmentDialog(true)}>
                Continue
              </Button>
            </Box>
          )}
        </>
      );
    }

    return (
      <>
        {dropTarget}
        <VendorAssignmentDialog {...panelProps} />
      </>
    );
  }
);
